import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Instances from './pages/Instances';
import InstanceDetail from './pages/InstanceDetail';
import QA from './pages/QA';
import Agent from './pages/Agent';
import Prompts from './pages/Prompts';
import Debug from './pages/Debug';
import Audit from './pages/Audit';
import Tenants from './pages/Tenants';
import Login, { getStoredToken, clearToken, getStoredUser } from './pages/Login';
import Users from './pages/Users';
import useStore from './store';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30000 }
  }
});

// ── Intercepta le chiamate API per includere il cookie di autenticazione ─────────
const API_BASE = process.env.REACT_APP_API_URL || '/api';
const originalFetch = window.fetch;
window.fetch = function(url, opts = {}) {
  if (typeof url === 'string' && url.startsWith(API_BASE)) {
    opts.credentials = opts.credentials || 'include';
  }
  return originalFetch(url, opts);
};

export default function App() {
  const [token, setToken] = useState(() => getStoredToken());
  const { setCurrentUser, clearCurrentUser, currentUser } = useStore();

  // Restore user from localStorage on mount
  useEffect(() => {
    const storedUser = getStoredUser();
    if (storedUser) setCurrentUser(storedUser);
  }, []); // eslint-disable-line

  // Controlla token ogni minuto
  useEffect(() => {
    const iv = setInterval(() => {
      const t = getStoredToken();
      if (!t) { setToken(null); clearCurrentUser(); }
    }, 60000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  function handleLogin(t, user) {
    setToken(t);
    if (user) setCurrentUser(user);
  }

  function handleLogout() {
    clearToken();
    setToken(null);
    clearCurrentUser();
    // Cancella il cookie httpOnly lato server
    fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
  }

  if (!token) {
    return (
      <QueryClientProvider client={queryClient}>
        <Login onLogin={handleLogin} />
        <Toaster position="bottom-right" toastOptions={toastOpts} />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Layout onLogout={handleLogout} />}>
            <Route index element={<Dashboard />} />
            <Route path="instances" element={<Instances />} />
            <Route path="instances/:id" element={<InstanceDetail />} />
            <Route path="qa" element={<QA />} />
            <Route path="qa/:instanceId" element={<QA />} />
            <Route path="agent" element={<Agent />} />
            <Route path="prompts" element={<Prompts />} />
            <Route path="debug" element={<Debug />} />
            <Route path="audit" element={<Audit />} />
            <Route path="tenants" element={<Tenants />} />
            {currentUser?.role === 'super_admin' && (
              <Route path="users" element={<Users />} />
            )}
            <Route path="*" element={<Navigate to="/" />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" toastOptions={toastOpts} />
    </QueryClientProvider>
  );
}

const toastOpts = {
  style: {
    background: '#0f0f14', color: '#e2e8f0',
    border: '1px solid #1e293b', borderRadius: '10px',
    fontFamily: 'JetBrains Mono, monospace', fontSize: '13px',
  }
};
